import { useQuery } from '@tanstack/react-query';
import { HISTORY } from 'constant';
import { getHistoryEntries } from 'api/history';
import { HistoryChangeEntry, HistoryEntry, HistoryType } from 'api/history/types';
import { convertToLocalDate } from 'utils/date-time-functions';

interface NormalizedHistoryChange {
  field: string;
  before: string;
  after: string;
  beforeStrikeThrough: boolean;
  afterStrikeThrough: boolean;
}

const normalizeText = (value: unknown): string => {
  if (value === null || value === undefined) {
    return '';
  }

  const text = String(value).trim();
  if (!text || text.toLowerCase() === 'null' || text.toLowerCase() === 'undefined') {
    return '';
  }

  return text;
};

const getNumberValue = (value: unknown): number => {
  if (typeof value === 'number' && Number.isFinite(value)) {
    return value;
  }

  if (typeof value === 'string') {
    const parsedValue = Number(value);
    if (Number.isFinite(parsedValue)) {
      return parsedValue;
    }
  }

  return 0;
};

const getBooleanValue = (value: unknown): boolean => {
  return value === true || value === 'true' || value === 1 || value === '1';
};

const parseTimestamp = (value: unknown) => {
  const timestampValue = normalizeText(value);
  if (!timestampValue) {
    return {
      createdDate: '-',
      createdTime: '-',
      timestampLabel: '-',
      timestampSortValue: 0,
    };
  }

  const parsedDate = new Date(timestampValue);
  if (Number.isNaN(parsedDate.getTime())) {
    return {
      createdDate: timestampValue,
      createdTime: '-',
      timestampLabel: timestampValue,
      timestampSortValue: 0,
    };
  }

  const localDate = convertToLocalDate(parsedDate);

  return {
    createdDate: localDate?.format('DD MMMM YYYY') || '-',
    createdTime: localDate?.format('hh:mm') || '-',
    timestampLabel: localDate?.format('MMM D, YYYY, hh:mm A') || '-',
    timestampSortValue: localDate?.valueOf() || parsedDate.getTime(),
  };
};

const getTimestampSource = (entry: HistoryEntry): unknown => {
  return (
    entry?.time ||
    entry?.timestamp ||
    entry?.changed_at ||
    entry?.changedAt ||
    entry?.event_time ||
    entry?.eventTime ||
    entry?.created_at ||
    entry?.createdAt ||
    entry?.updated_at ||
    entry?.updatedAt ||
    entry?.date
  );
};

const isChangeEntry = (entry: HistoryEntry): boolean => {
  return Boolean(
    normalizeText(entry?.display_name) ||
      normalizeText(entry?.displayName) ||
      normalizeText(entry?.field) ||
      normalizeText(entry?.label) ||
      normalizeText(entry?.name) ||
      normalizeText(entry?.key) ||
      normalizeText(entry?.title) ||
      normalizeText(entry?.field_name) ||
      normalizeText(entry?.fieldName) ||
      normalizeText(entry?.property) ||
      normalizeText(entry?.attribute) ||
      normalizeText(entry?.before) ||
      normalizeText(entry?.after) ||
      normalizeText(entry?.old) ||
      normalizeText(entry?.new) ||
      normalizeText(entry?.old_value) ||
      normalizeText(entry?.new_value) ||
      normalizeText(entry?.before_value) ||
      normalizeText(entry?.after_value),
  );
};

const getChangeSource = (entry: HistoryEntry): unknown => {
  const metadata =
    typeof entry?.metadata === 'object' && entry?.metadata
      ? (entry.metadata as Record<string, unknown>)
      : undefined;

  return (
    entry?.changes ||
    entry?.change_details ||
    entry?.details ||
    entry?.diffs ||
    entry?.diff ||
    entry?.change ||
    metadata?.changes ||
    metadata?.change_details ||
    (isChangeEntry(entry) ? entry : undefined)
  );
};

const normalizeHistoryChange = (value: unknown, fallbackField = ''): NormalizedHistoryChange | null => {
  if (!value || typeof value !== 'object') {
    return null;
  }

  const changeValue = value as HistoryChangeEntry;
  const field =
    normalizeText(changeValue.display_name) ||
    normalizeText(changeValue.displayName) ||
    normalizeText(changeValue.field) ||
    normalizeText(changeValue.label) ||
    normalizeText(changeValue.name) ||
    normalizeText(changeValue.key) ||
    normalizeText(changeValue.title) ||
    normalizeText(changeValue.field_name) ||
    normalizeText(changeValue.fieldName) ||
    normalizeText(changeValue.property) ||
    normalizeText(changeValue.attribute) ||
    fallbackField;

  const beforeValue =
    normalizeText(changeValue.before) ||
    normalizeText(changeValue.old) ||
    normalizeText(changeValue.old_value) ||
    normalizeText(changeValue.oldValue) ||
    normalizeText(changeValue.previous) ||
    normalizeText(changeValue.prev) ||
    normalizeText(changeValue.from) ||
    normalizeText(changeValue.before_value);

  const afterValue =
    normalizeText(changeValue.after) ||
    normalizeText(changeValue.new) ||
    normalizeText(changeValue.new_value) ||
    normalizeText(changeValue.newValue) ||
    normalizeText(changeValue.current) ||
    normalizeText(changeValue.curr) ||
    normalizeText(changeValue.to) ||
    normalizeText(changeValue.after_value);

  const hasAnyValue = Boolean(field || beforeValue || afterValue);
  if (!hasAnyValue) {
    return null;
  }

  return {
    field: field || 'Field',
    before: beforeValue || '-',
    after: afterValue || '-',
    beforeStrikeThrough:
      getBooleanValue(changeValue.before_strike_through) ||
      getBooleanValue(changeValue.beforeStrikeThrough),
    afterStrikeThrough:
      getBooleanValue(changeValue.after_strike_through) ||
      getBooleanValue(changeValue.afterStrikeThrough),
  };
};

const normalizeChanges = (value: unknown): NormalizedHistoryChange[] => {
  if (!value) {
    return [];
  }

  if (Array.isArray(value)) {
    return value.map((item) => normalizeHistoryChange(item)).filter(Boolean) as NormalizedHistoryChange[];
  }

  if (typeof value !== 'object') {
    return [];
  }

  if (isChangeEntry(value as HistoryEntry)) {
    const normalizedChange = normalizeHistoryChange(value);
    return normalizedChange ? [normalizedChange] : [];
  }

  return Object.entries(value).reduce<NormalizedHistoryChange[]>((accumulator, [key, fieldValue]) => {
    const normalizedChange = normalizeHistoryChange(fieldValue, key);
    if (!normalizedChange) {
      const displayValue = normalizeText(fieldValue);
      if (!displayValue) {
        return accumulator;
      }

      accumulator.push({
        field: key,
        before: '-',
        after: displayValue,
        beforeStrikeThrough: false,
        afterStrikeThrough: false,
      });
      return accumulator;
    }

    accumulator.push(normalizedChange);
    return accumulator;
  }, []);
};

const getActivityType = (entry: HistoryEntry, changeCount: number): string => {
  const explicitActivity =
    normalizeText(entry?.activity_type) ||
    normalizeText(entry?.activityType) ||
    normalizeText(entry?.event_type) ||
    normalizeText(entry?.eventType) ||
    normalizeText(entry?.action);

  if (explicitActivity) {
    return explicitActivity;
  }

  if (changeCount > 1) {
    return 'Multiple Updates';
  }

  const fallbackText = `${normalizeText(entry?.description)} ${normalizeText(entry?.what_changed)}`.toLowerCase();
  if (fallbackText.includes('create')) {
    return 'Created';
  }

  if (fallbackText.includes('delete') || fallbackText.includes('remove')) {
    return 'Deleted';
  }

  return 'Updated';
};

const getWhatChangedText = (entry: HistoryEntry, changeCount: number, changes: NormalizedHistoryChange[]): string => {
  if (changeCount > 1) {
    return `${changeCount} Changes`;
  }

  return (
    normalizeText(entry?.display_name) ||
    normalizeText(entry?.displayName) ||
    normalizeText(entry?.what_changed) ||
    normalizeText(entry?.whatChanged) ||
    normalizeText(entry?.summary) ||
    normalizeText(entry?.description) ||
    normalizeText(changes[0]?.field) ||
    '-'
  );
};

const useHistoryEntries = (type: HistoryType, id: string, tenantId?: string) => {
  const { isLoading: isHistoryDataLoading, data: historyRows } = useQuery({
    queryKey: [HISTORY, type, id, tenantId || ''],
    queryFn: () => getHistoryEntries(type, id, tenantId),
    enabled: Boolean(type && id),
    select: (data) => {
      if (!Array.isArray(data)) {
        return [];
      }

      return data.map((item, index) => {
        const timestamps = parseTimestamp(getTimestampSource(item));
        const changes = normalizeChanges(getChangeSource(item));
        const explicitChangeCount =
          getNumberValue(item?.change_count) ||
          getNumberValue(item?.changes_count) ||
          getNumberValue(item?.count);
        const changeCount = Math.max(explicitChangeCount, changes.length);
        const userName = normalizeText(item?.user_name) || normalizeText(item?.userName) || '-';
        const userInitial = userName === '-' ? '?' : userName.charAt(0).toUpperCase();
        const activityType = getActivityType(item, changeCount);
        const whatChanged = getWhatChangedText(item, changeCount, changes);

        return {
          id: normalizeText(item?.uuid) || normalizeText(item?.id) || `${type}-${id}-${index}`,
          createdDate: timestamps.createdDate,
          createdTime: timestamps.createdTime,
          userName,
          description: whatChanged,
          timestampLabel: timestamps.timestampLabel,
          timestampSortValue: timestamps.timestampSortValue,
          userInitial,
          activityType,
          whatChanged,
          changeCount,
          isMultipleChanges: changeCount > 1,
          changes,
          raw: item,
        };
      });
    },
  });

  return { isHistoryDataLoading, historyRows };
};

export default useHistoryEntries;
